import React from 'react';
import { t } from '@lingui/macro';

import type { SeoLocationDetails } from '@rover/rsdk/src/apiClient/latest/model/seoLocationDetails';
import type { LinkedLocation } from '@rover/types/src/apiResponses/FetchSeoLocationResponse';
import { SEO_LOCATION_REGEX } from '@rover/utilities/seoRegex';

import SeoLocationList from './SeoLocationList';

type Props = {
  linkedLocations: LinkedLocation[];
  topNeighborhoods: LinkedLocation[];
  seoLocation: Pick<SeoLocationDetails, 'relatedServices' | 'name' | 'serviceTypeName'> & {
    locality: Pick<SeoLocationDetails['locality'], 'cityUrl' | 'url'>;
  };
  indexUrl: string;
};

const SeoLinkedLocationsAndNeighborhoods: React.FC<Props> = ({
  linkedLocations,
  topNeighborhoods,
  seoLocation: { serviceTypeName, name: locationName, relatedServices, locality },
  indexUrl,
}) => {
  const linkedLocationsAreNeighborhoods = linkedLocations.every(({ absoluteUrl }) =>
    SEO_LOCATION_REGEX.NEIGHBORHOOD_SERVICE.test(new URL(absoluteUrl).pathname)
  );
  return (
    <div>
      <div data-qa-id="search-neighborhoods-list">
        <SeoLocationList
          indexUrl={indexUrl}
          titleHeader={t`Check out the ${serviceTypeName} in your neighborhood:`}
          seoLocations={topNeighborhoods}
          serviceTypeName={serviceTypeName}
          itemProp="isSimilarTo"
        />
      </div>
      <div data-qa-id="search-linked-locations-list">
        <SeoLocationList
          indexUrl={indexUrl}
          titleHeader={
            linkedLocationsAreNeighborhoods
              ? t`Rover also offers ${serviceTypeName} in these neighborhoods:`
              : t`Rover also offers ${serviceTypeName} in:`
          }
          seoLocations={linkedLocations}
          seeMoreUrl={locality.cityUrl ?? undefined}
          seeMoreName={locality.cityUrl ? t`See all neighborhoods…` : undefined}
          serviceTypeName={serviceTypeName}
          itemProp="isSimilarTo"
        />
      </div>
      <div data-qa-id="search-related-services-list">
        <SeoLocationList
          titleHeader={t`Rover also offers other services in ${locationName}:`}
          seoLocations={relatedServices.map((service) => ({
            absoluteUrl: service.url,
            locationName: service.internalLinkingTitle,
          }))}
          seeMoreUrl={locality.url}
          seeMoreName={t`See all services…`}
          itemProp="isRelatedTo"
        />
      </div>
    </div>
  );
};

export default SeoLinkedLocationsAndNeighborhoods;
